import isEmpty from 'lodash/isEmpty';
import React, { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { UniversalDocumentFields } from '@/features/ui/ProfileChapters';

import { SelectOption } from '@/shared/types';
import { FormTextInput, Grid, StepControlPanel, UKRAINE_PHONE_MASK_START } from '@/shared/ui';
import { MultipleFormInputPhone } from '@/shared/ui/InputPhone/MultipleFormInputPhone';

import { allAllowedToChoosePassportDocumentTypeCodes } from '@/components/Forms/constants';
import { profileDocumentDataInfoFieldsMapper } from '@/components/Forms/DocumentsData';

import { useERZDocumentTypes } from '@/modules/erz/helpers';

import { formatDate } from '@/helpers/dates';

import AuthContext from '@/contexts/Auth/AuthContext';

import { ExtractRedeliveryPersonalDataSchema } from '../../model/types';

export interface ExtractRedeliveryPersonalDataProps {
  stepToGo?: Nullable<number>;
  wasDraftAlreadyContinued?: boolean;
  specificStep?: (step: number) => void;
}
// TODO: Refactor MVSUSLUH-4833
export const ExtractRedeliveryPersonalData: React.FC<StepProps<ExtractRedeliveryPersonalDataProps>> = ({
  next,
  stepToGo,
  specificStep,
}) => {
  const { control, setValue, getValues, watch, formState } = useFormContext<ExtractRedeliveryPersonalDataSchema>();
  const { userData, documentsData } = useContext(AuthContext);

  const { data: passportDocumentTypeEntities = [] } = useERZDocumentTypes({
    allowedDocumentTypeCodes: allAllowedToChoosePassportDocumentTypeCodes,
  });

  const extractRedeliveryDocument = watch('extractRedeliveryDocument');
  const documents = watch('documents');

  useEffect(() => {
    if (!stepToGo) return;

    specificStep(stepToGo);
  }, [stepToGo]);

  useEffect(() => {
    const isDocumentFilledByUser = !isEmpty(extractRedeliveryDocument);

    if (!passportDocumentTypeEntities?.length || isDocumentFilledByUser) return;

    const { name_last, name_first, name_middle, contact_email, contact_phone } = userData || {};
    const currentValues = getValues();

    if (name_last && !currentValues.lastName) setValue('lastName', name_last);
    if (name_first && !currentValues.firstName) setValue('firstName', name_first);
    if (name_middle && !currentValues.middleName) setValue('middleName', name_middle);
    if (contact_email && !currentValues.email) setValue('email', contact_email);
    if (contact_phone && !currentValues.phones?.length && currentValues.phones?.[0] !== UKRAINE_PHONE_MASK_START)
      setValue('phones', [contact_phone]);

    const { passport } = documentsData;
    const documentType = passportDocumentTypeEntities?.find(({ code }: { code: string }) => code === passport?.type);

    // @ts-ignore
    setValue('extractRedeliveryDocument', documentType);
    setValue(`documents.${documentType?.code}.info`, profileDocumentDataInfoFieldsMapper(passport?.fields));
  }, [extractRedeliveryDocument, documents, passportDocumentTypeEntities]);

  return (
    <>
      <FormBlock title="Ваші особисті дані">
        <FormTextInput
          name="lastName"
          control={control}
          label="Прізвище"
          placeholder="Вкажіть прізвище"
          required
          disabled
        />
        <FormTextInput name="firstName" control={control} label="Імʼя" placeholder="Вкажіть імʼя" required disabled />
        <FormTextInput
          name="middleName"
          control={control}
          label="По батькові"
          placeholder="Вкажіть по батькові"
          disabled
        />
        <FormTextInput name="email" placeholder="example@example.com" control={control} label="Email" required />
        <MultipleFormInputPhone
          onChange={(phones) => phones && setValue('phones', phones)}
          maxPhoneNumbersQuantity={1}
          value={getValues()?.phones}
          errors={formState.errors?.phones as Array<{ message: string }>}
          required
        />
      </FormBlock>
      <FormBlock>
        <UniversalDocumentFields
          key="documentFields"
          fieldName="extractRedeliveryDocument"
          documentTypeEntities={passportDocumentTypeEntities as SelectOption[]}
          layout="service"
          documentRequired
          control={control}
          watch={watch}
        />
      </FormBlock>
      <StepControlPanel next={next} />
    </>
  );
};

export const ExtractRedeliveryPersonalDataPreview: React.FC = () => {
  const { getValues } = useFormContext<ExtractRedeliveryPersonalDataSchema>();
  const {
    extractRedeliveryDocument: documentType,
    documents,
    email,
    firstName,
    lastName,
    phones,
    middleName,
  } = getValues();

  return (
    <>
      <Grid
        title="Ваші особисті дані"
        data={[
          ['Прізвище', lastName],
          ['Імʼя', firstName],
          ['По батькові', middleName],
          ['Email', email],
          ['Номер телефона', phones?.join(', ')],
        ]}
      />
      <Grid
        title="Ваші паспортні дані"
        data={
          [
            ['Тип документа, що посвідчує особу', documentType?.label],
            ['Серія та номер', documents[documentType?.code].info.serialNumberDoc],
            ['Орган видачі', documents[documentType?.code].info.auth_dep],
            ['Дата видачі', formatDate(documents[documentType?.code].info.date)],
            ['Термін дії', formatDate(documents[documentType?.code].info.expirationDate)],
            ['УНЗР', documents[documentType?.code].info.unzr],
          ].filter(([, value]) => !!value) as any
        }
      />
    </>
  );
};
